// ABOUTME: Evaluates configured data-scope access rules without exposing domain-specific checks.
// ABOUTME: Shared role consumers use the resulting message key to block unsupported actions.

import {DataScopeAccessCheck, getDataScopeConfiguration} from './DataScopeConfiguration'
import {DataScopes} from './ManageUsers.selectors'

export type DataScopeAccessMap = Record<
  string,
  {
    errorMessageKey: string
    inaccessibleValues: Set<string>
  }
>

const normalizeValues = (value: string | string[] | undefined): string[] => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const getAccessCheck = (dataScopeName: string): DataScopeAccessCheck | undefined => {
  const configuration = getDataScopeConfiguration(dataScopeName)
  return Object.prototype.hasOwnProperty.call(configuration, 'accessCheck')
    ? (configuration as typeof configuration & {accessCheck?: DataScopeAccessCheck}).accessCheck
    : undefined
}

export const getDataScopeAccessMap = async (
  rolesDataScopes: DataScopes[]
): Promise<DataScopeAccessMap> => {
  const valuesByDataScope = new Map<string, Set<string>>()

  for (const dataScopes of rolesDataScopes) {
    for (const [dataScopeName, value] of Object.entries(dataScopes)) {
      if (!getAccessCheck(dataScopeName)) continue

      const values = valuesByDataScope.get(dataScopeName) ?? new Set<string>()
      normalizeValues(value).forEach((item) => values.add(item))
      valuesByDataScope.set(dataScopeName, values)
    }
  }

  const accessMapEntries = await Promise.all(
    Array.from(valuesByDataScope.entries()).map(async ([dataScopeName, values]) => {
      const accessCheck = getAccessCheck(dataScopeName)
      if (!accessCheck) return null

      const inaccessibleValues = await accessCheck.getInaccessibleValues(Array.from(values))
      return [
        dataScopeName,
        {
          errorMessageKey: accessCheck.errorMessageKey,
          inaccessibleValues: new Set(inaccessibleValues)
        }
      ] as const
    })
  )

  return Object.fromEntries(accessMapEntries.filter((entry) => entry !== null))
}

export const getDataScopeAccessError = (
  dataScopes: DataScopes,
  accessMap: DataScopeAccessMap
): string | null => {
  for (const [dataScopeName, value] of Object.entries(dataScopes)) {
    const accessResult = accessMap[dataScopeName]
    if (!accessResult) continue

    if (normalizeValues(value).some((item) => accessResult.inaccessibleValues.has(item))) {
      return accessResult.errorMessageKey
    }
  }

  return null
}

export const hasDataScopeAccessCheck = (dataScopes: DataScopes): boolean => {
  return Object.keys(dataScopes).some((dataScopeName) => Boolean(getAccessCheck(dataScopeName)))
}

export const validateDataScopeAccess = async (dataScopes: DataScopes): Promise<string | null> => {
  const accessMap = await getDataScopeAccessMap([dataScopes])
  return getDataScopeAccessError(dataScopes, accessMap)
}